import React, { useState, useRef } from "react";
import { 
    Button, Typography, makeStyles, CircularProgress, Paper, Box, 
    Checkbox, InputBase, IconButton
} from "@material-ui/core";
import { 
    CloudUpload as CloudUploadIcon,
    Add as AddIcon,
    Close as CloseIcon,
    KeyboardArrowRight as ArrowRightIcon,
    KeyboardArrowLeft as ArrowLeftIcon,
    Check as CheckIcon,
    Replay as RetryIcon,
    Apps as AppsIcon,
    Flight as FlightIcon,
    MenuBook as BookIcon,
    CloudQueue as CloudQueueIcon,
    GetApp as DownloadIcon
} from "@material-ui/icons";
import { useFetchContext } from "../../common/FetchContext";
import { useAuthContext } from "../../common/AuthContext"; 
import { UseLSDBCloudUpload } from "../../common/services/fileServices";

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(4), backgroundColor: "#f9fafc", minHeight: "100vh", fontFamily: "sans-serif", display: "flex", flexDirection: "column" },
    header: { marginBottom: theme.spacing(3) },
    dropzone: { 
        border: "2px dashed #a8c7fa", borderRadius: theme.spacing(2), backgroundColor: "#fafdff", 
        padding: theme.spacing(6, 2), display: "flex", flexDirection: "column", alignItems: "center", 
        justifyContent: "center", transition: "all 0.2s ease", marginBottom: theme.spacing(4), 
        "&.active": { backgroundColor: "#edf4ff", border: "2px solid #2563eb" } // Solid border when active
    },
    dragActiveIconWrapper: {
        backgroundColor: "#2563eb", borderRadius: theme.spacing(1.5), padding: theme.spacing(2.5),
        display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", marginBottom: theme.spacing(2)
    },
    miniDropzone: { border: "2px dashed #a8c7fa", borderRadius: theme.spacing(1.5), backgroundColor: "#fafdff", padding: theme.spacing(2), display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: theme.spacing(3), gap: theme.spacing(2), transition: "all 0.2s ease", "&.active": { backgroundColor: "#edf4ff", border: "2px solid #2563eb" } },
    miniCloudWrapper: { backgroundColor: "#eef2ff", borderRadius: theme.spacing(1), padding: theme.spacing(0.75), color: "#2563eb", display: "flex" },
    cloudIconWrapper: { backgroundColor: "#eef2ff", borderRadius: theme.spacing(1.5), padding: theme.spacing(1.5), marginBottom: theme.spacing(2), color: "#2563eb" },
    chooseButton: { backgroundColor: "#2563eb", color: "#fff", textTransform: "none", fontWeight: 600, padding: theme.spacing(1, 3), borderRadius: theme.spacing(1), margin: theme.spacing(2, 0), "&:hover": { backgroundColor: "#1d4ed8" } },
    listPaper: { borderRadius: theme.spacing(1), border: "1px solid #e5e7eb", backgroundColor: "#fff", overflow: "hidden", marginBottom: theme.spacing(4) },
    listHeader: { backgroundColor: "#f8fafc", padding: theme.spacing(2, 3), borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" },
    listItem: { display: "flex", alignItems: "center", padding: theme.spacing(1.5, 3), borderBottom: "1px solid #e5e7eb", "&:last-child": { borderBottom: "none" } },
    fileIconSquare: { width: 36, height: 36, borderRadius: theme.spacing(1), display: "flex", alignItems: "center", justifyContent: "center", marginRight: theme.spacing(2), color: "#fff", fontWeight: "bold", fontSize: "0.65rem", letterSpacing: 0.5 },
    bulkToolbar: { display: "flex", alignItems: "center", padding: theme.spacing(1.5, 2), border: "1px solid #e5e7eb", borderRadius: theme.spacing(1), backgroundColor: "#fff", marginBottom: theme.spacing(3), gap: theme.spacing(2) },
    bulkInputWrapper: { flex: 1, backgroundColor: "#f9fafc", border: "1px solid #e5e7eb", borderRadius: theme.spacing(1), padding: theme.spacing(0.5, 1.5) },
    bulkApplyBtn: { backgroundColor: "#2563eb", color: "#fff", textTransform: "none", fontWeight: 600, padding: theme.spacing(0.5, 2), borderRadius: theme.spacing(1), "&:hover": { backgroundColor: "#1d4ed8" }, "&.Mui-disabled": { backgroundColor: "#93c5fd", color: "#fff" } },
    actionBtn: { textTransform: "none", fontWeight: 600, borderColor: "#d1d5db", color: "#374151", padding: theme.spacing(0.5, 2), borderRadius: theme.spacing(1) },
    captionTableHeader: { display: "flex", padding: theme.spacing(1.5, 3), backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb" },
    captionColHeader: { fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase" },
    captionRow: { display: "flex", alignItems: "center", padding: theme.spacing(1, 2), borderBottom: "1px solid #e5e7eb", transition: "background-color 0.15s ease", "&:last-child": { borderBottom: "none" }, "&.selected": { backgroundColor: "#eff6ff" }, "&.warning": { backgroundColor: "#fffbeb" }, "&.failed": { backgroundColor: "#fef2f2" } },
    captionInput: { width: "100%", border: "1px solid #d1d5db", borderRadius: theme.spacing(0.5), padding: theme.spacing(1, 1.5), backgroundColor: "#fff", fontSize: "0.875rem", "&.warning": { borderColor: "#f59e0b", color: "#d97706" } },
    
    footer: { 
        position: "sticky", bottom: 0, backgroundColor: "#fff", borderTop: "1px solid #e5e7eb", 
        padding: theme.spacing(2, 4), display: "flex", justifyContent: "space-between", 
        alignItems: "center", zIndex: 10, marginLeft: theme.spacing(-4), marginRight: theme.spacing(-4), 
        marginBottom: theme.spacing(-4), marginTop: "auto"
    },
    
    footerContinueBtn: { backgroundColor: "#2563eb", color: "#fff", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#1d4ed8" }, "&.Mui-disabled": { backgroundColor: "#cbd5e1", color: "#fff" } },
    infoBanner: { backgroundColor: "#f0f5ff", border: "1px solid #dbeafe", borderRadius: theme.spacing(1), padding: theme.spacing(2), display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(3) },
    bannerBtn: { backgroundColor: "#fff", color: "#2563eb", textTransform: "none", fontWeight: 600, borderColor: "#dbeafe", marginLeft: theme.spacing(1) },
    globalProgressContainer: { marginBottom: theme.spacing(2) },
    globalProgressBar: { height: 6, backgroundColor: "#e5e7eb", borderRadius: 3, width: "100%", overflow: "hidden", marginTop: theme.spacing(1) },
    globalProgressFill: { height: "100%", backgroundColor: "#2563eb", transition: "width 0.3s ease" },
    itemProgressBar: { height: 4, backgroundColor: "#e5e7eb", borderRadius: 2, width: "100%", overflow: "hidden", marginTop: 4 },
    itemProgressFill: { height: "100%", backgroundColor: "#2563eb", transition: "width 0.2s ease" },
    statusText: { fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 },
    successContainer: { maxWidth: 700, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: theme.spacing(4) },
    successIconWrapper: { width: 64, height: 64, borderRadius: "50%", backgroundColor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: theme.spacing(2) },
    summaryCard: { display: "flex", justifyContent: "space-between", backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: theme.spacing(1), padding: theme.spacing(3), width: "100%", marginTop: theme.spacing(4) },
    summaryCol: { flex: 1 },
    summaryLabel: { fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 },
    summaryValue: { fontSize: "0.875rem", fontWeight: 600, color: "#1f2937" },
    whatNextLabel: { fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase", width: "100%", textAlign: "left", marginTop: theme.spacing(6), marginBottom: theme.spacing(2) },
    actionCardRow: { display: "flex", alignItems: "center", backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: theme.spacing(1), padding: theme.spacing(2.5, 3), width: "100%", marginBottom: theme.spacing(1.5), cursor: "pointer", transition: "all 0.2s", "&:hover": { borderColor: "#bfdbfe", backgroundColor: "#f8fafc" } },
    actionCardIconWrapper: { backgroundColor: "#eff6ff", color: "#2563eb", borderRadius: theme.spacing(1), padding: theme.spacing(1.5), display: "flex", alignItems: "center", justifyContent: "center", marginRight: theme.spacing(2) },
    typePillRow: { display: "flex", gap: 4, marginTop: theme.spacing(3) },
    typePill: { padding: "4px 8px", borderRadius: 4, color: "#fff", fontWeight: "bold", fontSize: "0.65rem", letterSpacing: 0.5 }
}));

const formatBytes = (bytes: number) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDateToYYYYMMDDHHMM = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getFileInfo = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'heic'].includes(ext)) return { type: "Photo", color: "#374151", isPhoto: true };
    if (ext === 'pdf') return { type: "PDF", color: "#ef4444", isPhoto: false };
    if (['doc', 'docx'].includes(ext)) return { type: "DOC", color: "#3b82f6", isPhoto: false };
    if (['zip', 'rar'].includes(ext)) return { type: "ZIP", color: "#f59e0b", isPhoto: false };
    if (['xls', 'xlsx', 'csv'].includes(ext)) return { type: "XLS", color: "#10b981", isPhoto: false };
    return { type: ext.toUpperCase() || "FILE", color: "#6b7280", isPhoto: false };
};

type ViewState = 'upload' | 'staged' | 'caption' | 'uploading' | 'success';
type FileStatus = 'pending' | 'uploading' | 'success' | 'failed';

type FileData = { 
    id: string; file: File; caption: string; isSelected: boolean; 
    isPhoto: boolean; status: FileStatus; progress: number; errorMsg?: string;
};

export const UploadLSDBCloud = () => {
    const classes = useStyles();
    const { authAxios } = useFetchContext();
    const { isAuthenticated, state: authState } = useAuthContext();
    const { mutateAsync: uploadFileAsync } = UseLSDBCloudUpload();
    
    const [view, setView] = useState<ViewState>('upload');
    const [isReading, setIsReading] = useState(false);
    
    const [filesData, setFilesData] = useState<FileData[]>([]);
    const [bulkCaptionText, setBulkCaptionText] = useState("");
    const [uploadCompleteTime, setUploadCompleteTime] = useState<Date | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Advanced Drag State
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const [dragStats, setDragStats] = useState({ total: 0, photos: 0, files: 0 });

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragCounter.current++;
        
        if (dragCounter.current === 1) {
            setIsDragging(true);
            
            // Inspect DataTransfer items before drop to show accurate UI text
            if (e.dataTransfer.items) {
                let photosCount = 0;
                let filesCount = 0;
                Array.from(e.dataTransfer.items).forEach(item => {
                    if (item.kind === 'file') {
                        if (item.type.startsWith('image/')) photosCount++;
                        else filesCount++;
                    }
                });
                setDragStats({ total: photosCount + filesCount, photos: photosCount, files: filesCount });
            }
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        if (e.dataTransfer.files) handleFilesAdded(e.dataTransfer.files);
    };

    const handleFilesAdded = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        if (fileArray.length === 0) return;
        setIsReading(true);
        setView('staged');
        setTimeout(() => {
            const newFilesData: FileData[] = fileArray.map(file => ({
                id: Math.random().toString(36).substring(7),
                file, caption: "", isSelected: false, isPhoto: getFileInfo(file).isPhoto,
                status: 'pending', progress: 0
            }));
            setFilesData(prev => [...prev, ...newFilesData]);
            setIsReading(false);
        }, 800);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) handleFilesAdded(e.target.files);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (id: string) => {
        setFilesData(prev => {
            const updated = prev.filter(f => f.id !== id);
            if (updated.length === 0) setView('upload');
            return updated;
        });
    };

    const toggleSelection = (id: string) => setFilesData(prev => prev.map(f => f.id === id ? { ...f, isSelected: !f.isSelected } : f));
    const updateCaption = (id: string, text: string) => setFilesData(prev => prev.map(f => f.id === id ? { ...f, caption: text } : f));
    const selectAllType = (type: 'photos' | 'files') => setFilesData(prev => prev.map(f => ({ ...f, isSelected: type === 'photos' ? f.isPhoto : !f.isPhoto })));
    const applyBulkCaption = () => {
        if (!bulkCaptionText.trim()) return;
        setFilesData(prev => prev.map(f => f.isSelected ? { ...f, caption: bulkCaptionText, isSelected: false } : f));
        setBulkCaptionText("");
    };

    const executeUpload = async (fileData: FileData) => {
        if (fileData.status === 'success') return;
        setFilesData(prev => prev.map(f => f.id === fileData.id ? { ...f, status: 'uploading', progress: 0, errorMsg: undefined } : f));

        try {
            await uploadFileAsync({
                authAxios,
                file: fileData.file,
                caption: fileData.caption,
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setFilesData(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: percentCompleted } : f));
                    }
                }
            });
            setFilesData(prev => prev.map(f => f.id === fileData.id ? { ...f, status: 'success', progress: 100 } : f));
        } catch (error: any) {
            setFilesData(prev => prev.map(f => f.id === fileData.id ? { ...f, status: 'failed', errorMsg: error.message } : f));
        }
    };

    const handleFinalUpload = () => {
        if (filesMissingCaption > 0) return;
        setView('uploading');
        filesData.forEach(fileData => {
            if (fileData.status !== 'success') executeUpload(fileData);
        });
    };

    const handleKeepWorking = () => {
        setUploadCompleteTime(new Date()); 
        setView('success');
    };

    const handleResetUpload = () => {
        setFilesData([]);
        setUploadCompleteTime(null);
        setView('upload');
    };

    const totalSize = filesData.reduce((acc, f) => acc + f.file.size, 0);
    const photoCount = filesData.filter(f => f.isPhoto).length;
    const fileCount = filesData.length - photoCount;
    const selectedCount = filesData.filter(f => f.isSelected).length;
    const filesMissingCaption = filesData.filter(f => !f.isPhoto && !f.caption.trim()).length;

    const uploadedFiles = filesData.filter(f => f.status === 'success');
    const uploadedCount = uploadedFiles.length;
    const failedCount = filesData.filter(f => f.status === 'failed').length;
    const uploadingCount = filesData.filter(f => f.status === 'uploading').length;
    const uploadedBytes = filesData.reduce((acc, f) => acc + (f.file.size * (f.progress / 100)), 0);
    const globalProgressPct = totalSize > 0 ? (uploadedBytes / totalSize) * 100 : 0;
    
    const uniqueUploadedTypes = Array.from(new Set(uploadedFiles.filter(f => !f.isPhoto).map(f => getFileInfo(f.file).type)));
    const uploadedPhotosCount = uploadedFiles.filter(f => f.isPhoto).length;
    const uploadedFilesCount = uploadedFiles.filter(f => !f.isPhoto).length;
    const captionedSetCount = uploadedFiles.filter(f => f.caption.trim().length > 0).length;

    const isAllSuccess = filesData.length > 0 && uploadedCount === filesData.length;

    return (
        <div className={classes.root}>
            <input id="hidden-file-input" type="file" multiple hidden ref={fileInputRef} onChange={handleFileChange} />

            {view !== 'success' && (
                <div className={classes.header}>
                    <Typography variant="h5" style={{ fontWeight: 600, color: "#111827", marginBottom: 4 }}>
                        {view === 'uploading' ? `Uploading ${filesData.length} items` 
                         : view === 'caption' ? `Name and caption ${filesData.length} files` 
                         : 'Upload to LSDB Cloud'}
                    </Typography>
                    {view !== 'uploading' && (
                        <Typography variant="body2" style={{ color: "#6b7280" }}>
                            {view === 'caption' ? "Add a caption. Nothing uploads until you click Done."
                             : view === 'staged' ? "Pick photos and files from this computer. You'll name and caption them next."
                             : "Add photos and files from this computer. They go into one shared library every module can pull from."}
                        </Typography>
                    )}
                </div>
            )}

            {/* VIEW 5: SUCCESS STATE */}
            {view === 'success' && (
                <div className={classes.successContainer}>
                    <div className={classes.successIconWrapper}>
                        <CheckIcon style={{ fontSize: 32, color: "#16a34a" }} />
                    </div>
                    
                    <Typography variant="h5" style={{ fontWeight: 600, color: "#111827", marginBottom: 8 }}>
                        {uploadedCount} items uploaded
                    </Typography>
                    
                    <Typography variant="body2" style={{ color: "#6b7280" }}>
                        {uploadedPhotosCount} photos and {uploadedFilesCount} files saved to LSDB Cloud. 
                        {failedCount > 0 && (
                            <span style={{ marginLeft: 4 }}>
                                {failedCount} failed – <span style={{ color: "#2563eb", fontWeight: 600, cursor: "pointer" }} onClick={() => setView('uploading')}>Retry</span>
                            </span>
                        )}
                    </Typography>

                    {uniqueUploadedTypes.length > 0 && (
                        <div className={classes.typePillRow}>
                            {uniqueUploadedTypes.map(type => {
                                const mockFile = new File([""], `dummy.${type.toLowerCase()}`);
                                const info = getFileInfo(mockFile);
                                return (
                                    <div key={type} className={classes.typePill} style={{ backgroundColor: info.color }}>
                                        {type}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className={classes.summaryCard}>
                        <div className={classes.summaryCol}>
                            <Typography className={classes.summaryLabel}>UPLOADED BY</Typography>
                            <Typography className={classes.summaryValue}>
                                {authState?.user || authState?.user || "Unknown User"}
                            </Typography>
                        </div>
                        <div className={classes.summaryCol}>
                            <Typography className={classes.summaryLabel}>DATE & TIME</Typography>
                            <Typography className={classes.summaryValue}>
                                {uploadCompleteTime ? formatDateToYYYYMMDDHHMM(uploadCompleteTime) : '--'}
                            </Typography>
                        </div>
                        <div className={classes.summaryCol}>
                            <Typography className={classes.summaryLabel}>CAPTIONS</Typography>
                            <Typography className={classes.summaryValue}>
                                {captionedSetCount} of {uploadedCount} set · {uploadedFilesCount > 0 && captionedSetCount >= uploadedFilesCount ? "all files captioned" : ""}
                            </Typography>
                        </div>
                    </div>

                    <Typography className={classes.whatNextLabel}>WHAT NEXT</Typography>

                    <div className={classes.actionCardRow}>
                        <div className={classes.actionCardIconWrapper}>
                            <AppsIcon />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: "#1f2937" }}>Add to a module</Typography>
                            <Typography variant="caption" style={{ color: "#9ca3af" }}>Module Intake, a test, an NCR, or a flag — pick where these {uploadedCount} go</Typography>
                        </div>
                        <ArrowRightIcon style={{ color: "#9ca3af" }} />
                    </div>

                    <div className={classes.actionCardRow}>
                        <div className={classes.actionCardIconWrapper}>
                            <CloudQueueIcon />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: "#1f2937" }}>Open in LSDB Cloud</Typography>
                            <Typography variant="caption" style={{ color: "#9ca3af" }}>See them in the library</Typography>
                        </div>
                        <ArrowRightIcon style={{ color: "#9ca3af" }} />
                    </div>

                    <div className={classes.actionCardRow} onClick={handleResetUpload}>
                        <div className={classes.actionCardIconWrapper}>
                            <AddIcon />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: "#1f2937" }}>Upload more</Typography>
                            <Typography variant="caption" style={{ color: "#9ca3af" }}>Back to the drop zone</Typography>
                        </div>
                        <ArrowRightIcon style={{ color: "#9ca3af" }} />
                    </div>
                </div>
            )}

            {/* VIEW 4: UPLOADING STATE */}
            {view === 'uploading' && (
                <>
                    <div className={classes.infoBanner}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <div className={classes.miniCloudWrapper} style={{ backgroundColor: "#fff", marginRight: 16 }}>
                                <CloudQueueIcon />
                            </div>
                            <div>
                                <Typography variant="body2" style={{ fontWeight: 600, color: "#1e3a8a" }}>You can keep working while this uploads</Typography>
                                <Typography variant="caption" style={{ color: "#475569" }}>Uploading continues in the background. Use any other operation — we'll tell you when it's done.</Typography>
                            </div>
                        </div>
                        <div>
                            <Button variant="outlined" className={classes.bannerBtn} startIcon={<AppsIcon />}>Module Intake</Button>
                            <Button variant="outlined" className={classes.bannerBtn} startIcon={<FlightIcon />}>Virtual Traveler</Button>
                            <Button variant="outlined" className={classes.bannerBtn} startIcon={<BookIcon />}>Writer's Agenda</Button>
                        </div>
                    </div>

                    <div className={classes.globalProgressContainer}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                            <div style={{ display: "flex", gap: 16 }}>
                                {uploadedCount > 0 && <Typography variant="caption" style={{ fontWeight: 600, color: "#16a34a" }}>{uploadedCount} uploaded</Typography>}
                                {uploadingCount > 0 && <Typography variant="caption" style={{ fontWeight: 600, color: "#2563eb" }}>{uploadingCount} uploading</Typography>}
                                {failedCount > 0 && <Typography variant="caption" style={{ fontWeight: 600, color: "#dc2626" }}>{failedCount} failed</Typography>}
                            </div>
                            <Typography variant="caption" style={{ color: "#9ca3af", fontFamily: "monospace" }}>
                                {formatBytes(uploadedBytes)} of {formatBytes(totalSize)}
                            </Typography>
                        </div>
                        <div className={classes.globalProgressBar}>
                            <div className={classes.globalProgressFill} style={{ width: `${globalProgressPct}%` }} />
                        </div>
                    </div>

                    <Paper elevation={0} className={classes.listPaper}>
                        <div className={classes.captionTableHeader}>
                            <div className={classes.captionColHeader} style={{ flex: 1.5 }}>FILE</div>
                            <div className={classes.captionColHeader} style={{ flex: 2 }}>CAPTION</div>
                            <div className={classes.captionColHeader} style={{ flex: 1 }}>STATUS</div>
                        </div>

                        <Box>
                            {filesData.map((fd) => {
                                const info = getFileInfo(fd.file);
                                const isFailed = fd.status === 'failed';
                                return (
                                    <div key={fd.id} className={`${classes.captionRow} ${isFailed ? 'failed' : ''}`}>
                                        <div style={{ flex: 1.5, display: "flex", alignItems: "center" }}>
                                            <div className={classes.fileIconSquare} style={{ backgroundColor: info.color }}>{!info.isPhoto && info.type}</div>
                                            <div>
                                                <Typography variant="body2" style={{ fontWeight: 600, color: "#374151", fontFamily: "monospace" }}>{fd.file.name}</Typography>
                                                <Typography variant="caption" style={{ color: "#9ca3af" }}>{formatBytes(fd.file.size)}</Typography>
                                            </div>
                                        </div>
                                        <div style={{ flex: 2, paddingRight: 16 }}>
                                            <Typography variant="body2" style={{ color: fd.caption ? "#374151" : "#cbd5e1" }}>
                                                {fd.caption || "No caption"}
                                            </Typography>
                                        </div>
                                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <div style={{ flex: 1 }}>
                                                {fd.status === 'success' && <span className={classes.statusText} style={{ color: "#16a34a" }}><CheckIcon fontSize="small" /> Uploaded</span>}
                                                {fd.status === 'failed' && <span className={classes.statusText} style={{ color: "#dc2626" }}>Failed · network</span>}
                                                {fd.status === 'uploading' && (
                                                    <div>
                                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                            <span className={classes.statusText} style={{ color: "#2563eb", fontSize: "0.75rem" }}>Uploading..</span>
                                                            <span style={{ color: "#2563eb", fontSize: "0.75rem", fontWeight: 600 }}>{fd.progress}%</span>
                                                        </div>
                                                        <div className={classes.itemProgressBar}>
                                                            <div className={classes.itemProgressFill} style={{ width: `${fd.progress}%` }} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <IconButton size="small" style={{ marginLeft: 8 }}>
                                                {fd.status === 'failed' ? (
                                                    <RetryIcon fontSize="small" style={{ color: "#2563eb" }} onClick={() => executeUpload(fd)} />
                                                ) : (
                                                    fd.status === 'uploading' ? <CloseIcon fontSize="small" style={{ color: "#9ca3af" }} /> : null
                                                )}
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })}
                        </Box>
                    </Paper>

                    <div className={classes.footer}>
                        {uploadingCount > 0 ? (
                             <Button variant="outlined" style={{ color: "#dc2626", borderColor: "#fecaca", textTransform: "none", fontWeight: 600 }}>Cancel remaining</Button>
                        ) : <div />}
                        
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            {!isAllSuccess && (
                                <Typography variant="body2" style={{ color: "#9ca3af" }}>Retry the failed file anytime from the upload tray</Typography>
                            )}
                            <Button variant="contained" className={classes.footerContinueBtn} onClick={handleKeepWorking}>
                                {isAllSuccess ? `Done. upload (${filesData.length})` : 'Keep working'}
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {/* VIEW 1: LOADING SKELETON */}
            {isReading && (
                <>
                    <div className={classes.miniDropzone}>
                        <CircularProgress size={20} thickness={5} style={{ color: "#2563eb" }} />
                        <Typography variant="body2" style={{ fontWeight: 600, color: "#1f2937" }}>Reading files...</Typography>
                    </div>
                    <Paper elevation={0} style={{ padding: 24, border: "1px solid #e5e7eb" }}><CircularProgress size={24} /></Paper>
                </>
            )}

            {/* VIEW 2: STAGED LIST */}
            {view === 'staged' && !isReading && (
                <>
                    <div 
                        className={`${classes.miniDropzone} ${isDragging ? "active" : ""}`}
                        onDragEnter={handleDragEnter}
                        onDragOver={(e) => e.preventDefault()}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {isDragging ? (
                             <Typography variant="body2" style={{ fontWeight: 600, color: "#2563eb" }}>
                                Drop files to add to queue
                             </Typography>
                        ) : (
                            <>
                                <div className={classes.miniCloudWrapper}><CloudUploadIcon fontSize="small" /></div>
                                <Typography variant="body2" style={{ fontWeight: 600, color: "#1f2937" }}>Drop more files here</Typography>
                                <Typography variant="body2" style={{ color: "#9ca3af" }}>or</Typography>
                                <Button variant="outlined" style={{ borderColor: "#2563eb", color: "#2563eb", textTransform: "none", fontWeight: 600 }} onClick={() => fileInputRef.current?.click()}>
                                    Add more files
                                </Button>
                            </>
                        )}
                    </div>

                    <Paper elevation={0} className={classes.listPaper}>
                        <div className={classes.listHeader}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#111827" }}>{filesData.length} {filesData.length === 1 ? 'file' : 'files'} selected</Typography>
                                <Typography variant="caption" style={{ color: "#9ca3af" }}>{photoCount} photos · {fileCount} files · {formatBytes(totalSize)}</Typography>
                            </div>
                            <Typography variant="caption" style={{ fontWeight: 600, color: "#ef4444", cursor: "pointer" }} onClick={() => { setFilesData([]); setView('upload'); }}>Clear all</Typography>
                        </div>
                        <Box>
                            {filesData.map((fd) => {
                                const info = getFileInfo(fd.file);
                                return (
                                    <div key={fd.id} className={classes.listItem}>
                                        <div className={classes.fileIconSquare} style={{ backgroundColor: info.color }}>{!info.isPhoto && info.type}</div>
                                        <Typography variant="body2" style={{ fontWeight: 600, flex: 1, color: "#374151" }}>{fd.file.name}</Typography>
                                        <Typography variant="body2" style={{ color: "#9ca3af", width: 80 }}>{info.type}</Typography>
                                        <Typography variant="body2" style={{ color: "#9ca3af", width: 80, textAlign: "right" }}>{formatBytes(fd.file.size)}</Typography>
                                        <IconButton size="small" style={{ marginLeft: 16 }} onClick={() => handleRemoveFile(fd.id)}><CloseIcon fontSize="small" /></IconButton>
                                    </div>
                                );
                            })}
                        </Box>
                    </Paper>

                    <div className={classes.footer}>
                        <Typography variant="body2" style={{ color: "#9ca3af" }}>Nothing is uploaded yet — next you can rename files and add captions.</Typography>
                        <div>
                            <Button variant="outlined" style={{ marginRight: 16, textTransform: "none", fontWeight: 600 }} onClick={() => { setFilesData([]); setView('upload'); }}>Cancel</Button>
                            <Button variant="contained" className={classes.footerContinueBtn} endIcon={<ArrowRightIcon />} onClick={() => setView('caption')}>Continue with {filesData.length} files</Button>
                        </div>
                    </div>
                </>
            )}

            {/* VIEW 3: CAPTION & NAME SCREEN */}
            {view === 'caption' && !isReading && (
                <>
                    <div className={classes.bulkToolbar}>
                        <Typography variant="caption" style={{ color: "#2563eb", fontWeight: 700, width: 80 }}>{selectedCount} SELECTED</Typography>
                        <div className={classes.bulkInputWrapper}>
                            <InputBase placeholder='Caption for selected items — e.g. "Crate 12 — front label"' fullWidth style={{ fontSize: "0.875rem" }} value={bulkCaptionText} onChange={(e) => setBulkCaptionText(e.target.value)} />
                        </div>
                        <Button variant="contained" className={classes.bulkApplyBtn} disabled={selectedCount === 0 || !bulkCaptionText.trim()} onClick={applyBulkCaption}>Apply to {selectedCount}</Button>
                        
                    </div>

                    <Paper elevation={0} className={classes.listPaper}>
                        <div className={classes.captionTableHeader}>
                            <div className={classes.captionColHeader} style={{ width: 60 }}>ITEM</div>
                            <div className={classes.captionColHeader} style={{ flex: 1 }}>FILE NAME (extension can't change)</div>
                            <div className={classes.captionColHeader} style={{ flex: 2, paddingLeft: 16 }}>CAPTION</div>
                            <div className={classes.captionColHeader} style={{ width: 100, textAlign: "right", paddingRight: 32 }}>SIZE</div>
                        </div>

                        <Box>
                            {filesData.map((fd) => {
                                const info = getFileInfo(fd.file);
                                const missingRequired = !fd.isPhoto && !fd.caption.trim();
                                return (
                                    <div key={fd.id} className={`${classes.captionRow} ${fd.isSelected ? 'selected' : (missingRequired ? 'warning' : '')}`}>
                                        <div style={{ width: 60, display: "flex", alignItems: "center" }}>
                                            <Checkbox checked={fd.isSelected} onChange={() => toggleSelection(fd.id)} color="primary" size="small" />
                                        </div>
                                        <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                                            <div className={classes.fileIconSquare} style={{ backgroundColor: info.color }}>{!info.isPhoto && info.type}</div>
                                            <div>
                                                <Typography variant="body2" style={{ fontWeight: 600, color: "#374151" }}>{fd.file.name}</Typography>
                                                <Typography variant="caption" style={{ color: "#9ca3af" }}>{formatBytes(fd.file.size)}</Typography>
                                            </div>
                                        </div>
                                        <div style={{ flex: 2, paddingLeft: 16, paddingRight: 16 }}>
                                            <InputBase className={`${classes.captionInput} ${missingRequired ? 'warning' : ''}`} placeholder={missingRequired ? "Add a caption — required for files" : "Add a caption (optional)"} value={fd.caption} onChange={(e) => updateCaption(fd.id, e.target.value)} />
                                        </div>
                                        <div style={{ width: 100, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                            <Typography variant="caption" style={{ color: "#9ca3af", marginRight: 16 }}>{formatBytes(fd.file.size)}</Typography>
                                            <IconButton size="small" onClick={() => handleRemoveFile(fd.id)}><CloseIcon fontSize="small" style={{ color: "#9ca3af" }} /></IconButton>
                                        </div>
                                    </div>
                                );
                            })}
                        </Box>
                    </Paper>

                    <div className={classes.footer}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <Button variant="outlined" startIcon={<ArrowLeftIcon />} className={classes.actionBtn} onClick={() => setView('staged')}>Back</Button>
                            <Typography variant="body2" style={{ color: "#9ca3af" }}>Captions: required for files, optional for photos</Typography>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            {filesMissingCaption > 0 && <Typography variant="body2" style={{ color: "#dc2626", fontWeight: 600 }}>{filesMissingCaption} {filesMissingCaption === 1 ? 'file' : 'files'} still need a caption</Typography>}
                            <Button variant="contained" className={classes.footerContinueBtn} disabled={filesMissingCaption > 0} onClick={handleFinalUpload}>Upload {filesData.length}</Button>
                        </div>
                    </div>
                </>
            )}

            {/* VIEW 0: INITIAL EMPTY UPLOAD */}
            {view === 'upload' && !isReading && (
                <div 
                    className={`${classes.dropzone} ${isDragging ? "active" : ""}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {isDragging ? (
                        <>
                            <div className={classes.dragActiveIconWrapper}>
                                <DownloadIcon style={{ fontSize: 40 }} />
                            </div>
                            <Typography variant="h6" style={{ fontWeight: 600, color: "#2563eb" }}>
                                Drop to upload
                            </Typography>
                            <Typography variant="body2" style={{ color: "#6b7280", marginTop: 4 }}>
                                {dragStats.total > 0 
                                    ? `${dragStats.total} files · ${dragStats.photos} photos, ${dragStats.files} files — release to add them` 
                                    : 'Release to add files'}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <div className={classes.cloudIconWrapper}><CloudUploadIcon fontSize="large" /></div>
                            <Typography variant="h6" style={{ fontWeight: 600, color: "#1f2937" }}>Drag files or folders here</Typography>
                            <Typography variant="body2" style={{ color: "#9ca3af", margin: "8px 0" }}>or</Typography>
                            <Button className={classes.chooseButton} variant="contained" startIcon={<AddIcon />} onClick={() => fileInputRef.current?.click()}>Choose files</Button>
                            <Typography variant="caption" style={{ color: "#9ca3af", marginTop: 16 }}>Photos (JPG, PNG, HEIC) and files (PDF, DOC, XLS, CSV, ZIP, TXT) · no limit on how many</Typography>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};